import React, {createContext, useContext, useReducer, useMemo, useCallback} from 'react';
import {LoadingState, generateCode, groupReducer} from '../helper/utils';
import {Chat, MessageGroup, Message, Nullable, User} from "../models";
import {
    fetchChats,
    getMessages,
    sendMessage,
    readMessages,
    Read
} from '../services/chat';

type MessagePage = {id: string, token: Nullable<string>};
type ChatMessages = {lastUnread?: string, groups: MessageGroup[]}
type MessageUpdate = {
    chatId: string
    id: string
    value: {id?: string, status: Message["status"], timestamp?: number}
};
type  State = {
    chats: Chat[]
    currentChatId?: string
    inputMessage: string
    message?: {desc: string, title: string}
    messageCursors: Record<string, Nullable<string>>,
    status: {chats: LoadingState, messages: LoadingState}
    user?: User
}
type ChatAddition = {chatID: string, values: Message[]};
type MessageRead = {ids: string[], chatId: string, readerId: string}
type ChatAction =
| {type: "set_chats", payload: Chat[]}
| {type: "acknowledge", payload: MessageRead}
| {type: "add_chat", payload: Chat}
| {type: "set_user", payload: User}
| {type: "set_current_chat", payload: string | undefined}
| {type: "add_messages", payload: ChatAddition}
| {type: "set_error", payload?: {desc: string, title: string}}
| {type: "set_input_message", payload: string}
| {type: "set_status", payload: State["status"]}
| {type: "set_cursor", payload: MessagePage}
| {type: "update_message", payload: MessageUpdate};

const initialState: State = {
    chats: [],
    inputMessage: '',
    messageCursors: {},
    status: {chats: LoadingState.None, messages: LoadingState.None}
};

const ChatContext = createContext<ReturnType<typeof useChatValue> | undefined>(undefined);

function chatReducer(state: State, action: ChatAction): State {
    switch (action.type) {
        case "set_chats":
            return {
                ...state,
                chats: Chat.mergeChats(state.chats,action.payload)
            };
        case "add_chat":
            return {...state, chats: state.chats.concat([action.payload])}
        case "set_error": return {...state, message: action.payload};
        case "set_user":
            return {...state, user: action.payload};
        case "set_current_chat":{
            return {...state, currentChatId: action.payload};
        }
        case "add_messages":
            return {
                ...state,
                chats: state.chats.map(function (c) {
                    let res;
                    if (c.id === action.payload.chatID) {
                        res =  Chat.addMessages(c, action.payload.values);
                        return res;
                    }
                    return c;
                })
            };
        case "acknowledge": {
            const {chatId, ids, readerId} = action.payload
            return {
                ...state,
                chats: state.chats.map(function (c) {
                    let res;
                    if (c.id === chatId) {
                        res = Chat.acknowledgeMessages(c, readerId, ids);
                        return res;
                    }
                    return c;
                })
            };
        }
        case "update_message": {
            const {chatId, id, value} = action.payload;
            return {
                ...state,
                chats: state.chats.map(function (c) {
                    let res;
                    if (c.id === chatId) {
                        res = Chat.updateMessage(c, value, id);
                        return res;
                    }
                    return c;
                })
            };
        }
        case "set_input_message":
            return {...state, inputMessage: action.payload};
        case "set_status":
            return {...state, status: action.payload};
        case "set_cursor": {
            const {id, token} = action.payload;
            const clone = Object.assign({}, state.messageCursors);
            clone[id] = token;
            return {...state, messageCursors: clone};
        }
        default:
            return state;
    }
}

function createChatActions(dispatch: React.Dispatch<ChatAction>) {
    return {
        acknowledge: (payload: MessageRead) => dispatch(
            {type: "acknowledge", payload}
        ),
        addChat: (payload: Chat) => dispatch({type: "add_chat", payload}),
        clearError: () => dispatch({type: "set_error"}),
        setChats: (payload: Chat[]) => dispatch({type: "set_chats", payload}),
        setUser: (payload: User) => dispatch({type: "set_user", payload}),
        setCurrentChat: (payload?: string) => dispatch(
            {type: "set_current_chat", payload}
        ),
        addMessages: (payload: ChatAddition) => dispatch(
            {type: "add_messages", payload}
        ),
        setInputMessage: (payload: string) => dispatch(
            {type: "set_input_message", payload}
        ),
        setStatus: (payload: State["status"]) => dispatch(
            {type: "set_status", payload}
        ),
        updateMessage: (payload: MessageUpdate) => dispatch(
            {type: "update_message", payload}
        )
    };
}

function useChatValue() {
    const [state, dispatch] = useReducer(chatReducer, initialState);
    const actions = useMemo(() => createChatActions(dispatch), [dispatch]);
    const getChat = useCallback(async function () {
        let chats;
        updateChatStatus(LoadingState.Pending);
        try {
            chats = await fetchChats("active");
            actions.setChats(chats);
            updateChatStatus(LoadingState.Success);
        } catch (error) {
            updateChatStatus(LoadingState.Error);
        }
    }, [actions]);
    const fetchMessages = useCallback(async function (chatID: string) {
        let res;
        if (state.messageCursors[chatID] === null) {
            return;
        }
        updateMessageStatus(LoadingState.Pending);
        res = await getMessages(chatID, state.messageCursors[chatID]);
        if (res.type === "success") {
            actions.addMessages({chatID, values: res.value.messages});
            dispatch({
                type: "set_cursor",
                payload: {id: chatID, token: res.value.token}
            })
            updateMessageStatus(LoadingState.Success);
        } else {
            updateMessageStatus(LoadingState.Error);
            dispatch({type: "set_error", payload: {
                title: "Error fetching message",
                desc: res.message
            }})
        }
    }, [actions]);
    const acknowledge = useCallback(async function (chatId: string, messages: Read[]) {
        let res;
        if (!state.user) {
            console.error("No user set up");
            return;
        }
        res = await readMessages(chatId, messages);
        if (res.type === "success") {
            actions.acknowledge({
                chatId,
                ids: messages.map((m) => m.messageID),
                readerId: state.user.userID
            })
        }
    }, [state.user, actions]);
    const handleSendMessage = useCallback(async function () {
        let res;
        let msg: Message;
        const {currentChatId, inputMessage, user} = state;
        if (!inputMessage.trim() || !currentChatId || !user) {
            return;
        }
        msg = {
            chatID: currentChatId,
            content: inputMessage,
            id: generateCode(8),
            readers: [user.userID],
            senderId: user.userID,
            senderName: user.name,
            status: "pending",
            timestamp: Date.now()
        };
        actions.addMessages({chatID: currentChatId, values: [msg]});
        actions.setInputMessage('');
        res = await sendMessage(currentChatId, msg.content);
        if (res.type === "success") {
            actions.updateMessage({
                chatId: currentChatId,
                id: msg.id,
                value: Object.assign(res.value, {status: "sent"})
            });
        } else {
            actions.updateMessage({
                chatId: currentChatId,
                id: msg.id,
                value: {status: "failed"}
            });
            dispatch({type: "set_error", payload: {
                title: "Error sending message",
                desc: res.message
            }});
        }
    }, [state.inputMessage, state.currentChatId, state.user, actions]);
    const currentChat = useMemo(function () {
        const {currentChatId = ""} = state;
        const item = state.chats.filter((c) => c.id === currentChatId)[0];
        const groupedMessages: ChatMessages = {groups: []};
        let tmp;
        let unread: Message[];
        if (!item) {
            return;
        }
        unread = item.messages.filter(
            (m) => !m.readers.includes(state.user?.userID ?? "")
        );
        tmp = item.messages.reduce(
            groupReducer(state.user?.userID ?? ""),
            {messages: {}}
        );
        groupedMessages.lastUnread = tmp.lastUnreadId;
        groupedMessages.groups = Object.entries(tmp.messages).sort(
            (e1, e2) => e1[1][0].timestamp - e2[1][0].timestamp
        ).map((e) => Object.assign({}, {label: e[0], items: e[1]}));
        return {groupedMessages, item, unread};
    }, [state]);
    const canSend = useMemo(function () {
        let index: number;
        let status: Message["status"]|undefined;
        if (!currentChat) {
            return false;
        }
        index = currentChat.item.messages.length;
        status = currentChat.item.messages[index - 1]?.status
        if (!status) {
            return true;
        }
        return !["pending", "failed"].includes(status);
    }, [currentChat, state]);
    const unreadChats = useMemo(function () {
        return state.chats.reduce(function (
            acc: Record<string, number>,
            val: Chat
        ): Record<string, number> {
            acc[val.id] = val.messages.filter(
                (m) => !m.readers.includes(state.user?.email ?? "")
            ).length;
            return acc;
        }, Object.create(null));
    }, [state]);
    const resendMessage = useCallback(async function (val: Message) {
        const chatId = state.currentChatId;
        const user = state.user;
        let res;
        if (val.status !== "failed" || !chatId || !user) {
            return;
        }
        actions.updateMessage({chatId, id: val.id, value: {status: "pending"}});
        res = await sendMessage(chatId, val.content);
        if (res.type === "success") {
            actions.updateMessage({
                chatId,
                id: val.id,
                value: Object.assign(res.value, {status: "sent"})
            });
        } else {
            actions.updateMessage({
                chatId,
                id: val.id,
                value: {status: "failed"}
            });
        }
    }, [state.currentChatId, state.user, actions]);
    function updateMessageStatus(messages: LoadingState) {
        dispatch({type: "set_status", payload: {...state.status, messages}});
    }
    function updateChatStatus(chats: LoadingState) {
        dispatch({type: "set_status", payload: {...state.status, chats}});
    }

    return useMemo(() => ({
        acknowledge,
        actions,
        canSend,
        currentChat,
        fetchChats: getChat,
        fetchMessages,
        handleSendMessage,
        resendMessage,
        state,
        unreadChats
    }), [
        acknowledge,
        actions,
        canSend,
        currentChat,
        getChat,
        fetchMessages,
        handleSendMessage,
        resendMessage,
        state,
        unreadChats
    ]);
}

export function ChatProvider({children}: {children: React.ReactNode}) {
    const value = useChatValue();
    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
export function useChat() {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}
